/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import dayjs from "dayjs";
import { Loader } from "@nordcloud/gnui";
import { useCustomerConfigQuery } from "~/generated/graphql";
import { ERROR_TEXT } from "~/constants";
import { showError } from "~/services/toast";
import { includes } from "~/tools/array";
import { noop } from "~/tools/function";
import { Currency } from "~/tools/money/currencies";
import { Maybe } from "~/tools/types";
import {
  CurrencyLSKeys,
  isCachedCurrencyRateExpired,
  kantorQuery,
} from "./utils";

type CustomerConfig = {
  currency: Currency;
  defaultCurrency?: Currency;
  isDefaultPriority?: boolean;
  riCoverageTarget?: number;
  enabledFeatures?: string[];
};

type CustomerConfigInterface = {
  config: Maybe<CustomerConfig>;
  update: (newConfig: CustomerConfig) => void;
};

export const CustomerConfigContext = createContext<CustomerConfigInterface>({
  config: null,
  update: noop,
});
CustomerConfigContext.displayName = "CustomerConfigContext";

type CustomerConfigProviderProps = {
  children: ReactNode;
};

type PersistedCurrency = {
  currency?: string;
  rate?: number;
};

function persistCurrencySettings(input: PersistedCurrency) {
  if (input.currency) {
    localStorage.setItem(CurrencyLSKeys.CURRENCY, input.currency);
  }
  if (input.rate && input.currency) {
    const currencyRate = {
      rate: input.rate,
      expirationDate: dayjs().add(1, "day"),
    };
    localStorage.setItem(
      `${CurrencyLSKeys.RATE}${input.currency}`,
      JSON.stringify(currencyRate)
    );
  }
}

export function CustomerConfigProvider({
  children,
}: CustomerConfigProviderProps) {
  const storedCurrency = localStorage.getItem(CurrencyLSKeys.CURRENCY);
  const [config, setConfig] = useState<CustomerConfig>({
    currency: setISOCurrencyFromString(storedCurrency),
    defaultCurrency: Currency.Eur,
    riCoverageTarget: 0,
    enabledFeatures: [],
  });

  const { data, loading } = useCustomerConfigQuery();

  const defaultCurrency = useMemo(() => {
    const customerConfigCurrency = data?.customerConfig?.defaultCurrency;
    return setISOCurrencyFromString(customerConfigCurrency);
  }, [data]);

  useEffect(() => {
    if (data?.customerConfig == null) {
      return;
    }

    const riCoverageTarget = data?.customerConfig?.riCoverageTarget || 0;
    const enabledFeatures = data?.customerConfig?.enabledFeatures || [];
    const isDefaultPriority = defaultCurrency !== Currency.Eur;

    const storedId = localStorage.getItem(CurrencyLSKeys.PREV_ID);
    const hasCustomerIdChanged = storedId !== data.customerConfig.id;

    if (hasCustomerIdChanged) {
      localStorage.setItem(
        CurrencyLSKeys.CURRENCY,
        data?.customerConfig.defaultCurrency ?? Currency.Eur
      );
    }

    // Bugfix: Bypass for formatMoney function.
    // For default currency we lock apply rate (when default currencies are set then lock apply rate).
    localStorage.setItem(
      CurrencyLSKeys.DEFAULT_PRIORITY,
      isDefaultPriority.toString()
    );

    setConfig({
      currency: hasCustomerIdChanged
        ? defaultCurrency
        : setISOCurrencyFromString(storedCurrency),
      defaultCurrency,
      riCoverageTarget,
      enabledFeatures,
      isDefaultPriority,
    });
  }, [data, defaultCurrency]);

  const setCurrency = (input: PersistedCurrency) => {
    persistCurrencySettings(input);
    setConfig((prevConfig) => ({
      ...prevConfig,
      ...(input.currency != null && {
        currency: setISOCurrencyFromString(input.currency, prevConfig.currency),
      }),
    }));
  };

  useEffect(() => {
    async function getRates() {
      const isRateValid = isCachedCurrencyRateExpired(
        localStorage.getItem(`${CurrencyLSKeys.RATE}${config.currency}`)
      );
      if (!isRateValid) {
        try {
          const { exchangeRate } = await kantorQuery(config.currency);
          setCurrency({
            rate: exchangeRate,
            currency: config.currency,
          });
        } catch {
          showError(ERROR_TEXT.currencyRateRefresh);
          setCurrency({
            currency: defaultCurrency,
          });
        }
      }
    }

    if (config.currency !== Currency.Eur) {
      void getRates();
    } else {
      const { rate: cachedRate }: { rate: string } = JSON.parse(
        localStorage.getItem(`${CurrencyLSKeys.RATE}${config.currency}`) ?? "{}"
      );

      setCurrency({
        rate: parseFloat(cachedRate || "1"),
      });
    }
  }, [config.currency, defaultCurrency]);

  const updateConfig = useCallback(
    (newConfig: CustomerConfig) => {
      setConfig(newConfig);
      persistCurrencySettings({
        currency: newConfig.currency,
      });
    },
    [setConfig]
  );

  const value = useMemo(
    () => ({
      config,
      update: updateConfig,
    }),
    [config, updateConfig]
  );

  if (loading) {
    return <Loader isBackground />;
  }

  return (
    <CustomerConfigContext.Provider value={value}>
      {children}
    </CustomerConfigContext.Provider>
  );
}

export function useCustomerConfig() {
  const context = useContext(CustomerConfigContext);

  if (context === undefined) {
    throw new Error(
      "useCustomerConfig must be used within a CustomerConfigProvider"
    );
  }
  return context;
}

function setISOCurrencyFromString(
  currency: Maybe<string>,
  defaultCurrency = Currency.Eur
) {
  if (isISOCurrency(currency)) {
    return currency;
  }

  console.warn(
    `Wrong ISO currency ${
      currency ?? "'no currency provided'"
    }. Falling back to ${defaultCurrency}`
  );
  return defaultCurrency;
}

function isISOCurrency(currency: Maybe<string>): currency is Currency {
  return includes(Object.values(Currency), currency);
}
